import { useState, useEffect } from "react";
import { config } from "../../constants";
import axiosInstance from "../../utils/axios";

/**
 * Facebook OAuth Component
 *
 * Provides Facebook authentication button for TapCreatorForm.
 * Uses server-side redirect flow (redirects to backend which redirects to Facebook).
 */
const FacebookOAuth = ({ onStateUpdate, tap }) => {
  const [isButtonDisabled, setButtonDisabled] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [adAccounts, setAdAccounts] = useState([]);
  const [error, setError] = useState(null);

  // Listen for OAuth callback messages from popup
  useEffect(() => {
    const handleMessage = (event) => {
      // Security: Verify message origin comes from our taps backend
      const allowedOrigins = [
        config.url.TAPS_URL?.replace('/taps', ''),  // Production: https://api.datajolt.io
        'http://localhost:5000',                     // Local development
        'http://127.0.0.1:5000'
      ].filter(Boolean);

      if (!allowedOrigins.some(origin => event.origin.startsWith(origin))) {
        return; // Ignore messages from unknown origins
      }

      if (event.data?.type === 'facebook_oauth_success') {
        setIsSuccessful(true);
        setButtonDisabled(true);
        setAdAccounts(event.data.ad_accounts || []);
        if (onStateUpdate) {
          onStateUpdate({
            facebook_authenticated: true,
            facebook_ad_accounts: event.data.ad_accounts,
            airbyte_facebook_access_token: event.data.access_token,
          });
        }
      } else if (event.data?.type === 'facebook_oauth_error') {
        setError(event.data.message || 'Authentication failed');
        setButtonDisabled(false);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onStateUpdate]);

  const handleAuthClick = async () => {
    setButtonDisabled(true);
    setError(null);

    try {
      // Fetch the Facebook auth URL with authentication
      const response = await axiosInstance.get(`${config.url.TAPS_URL}/api/oauth/facebook/connect`);

      // Open centered popup window
      const width = 500, height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      window.open(
        response.data.auth_url,
        'Facebook Login',
        `width=${width},height=${height},left=${left},top=${top}`
      );
    } catch (err) {
      setError(err.message || "Failed to initiate Facebook authentication");
      setButtonDisabled(false);
    }
  };

  if (isSuccessful) {
    return (
      <div style={{ display: "flex", alignItems: "center", marginLeft: "10px" }}>
        <button className="facebook-auth-button facebook-auth-success" disabled>
          <div className="facebook-button-content">
            <div className="facebook-button-icon">
              <svg viewBox="0 0 24 24" style={{ width: "18px", height: "18px" }}>
                <path
                  fill="#28a745"
                  d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                />
              </svg>
            </div>
            <span className="facebook-button-text">
              Authorized with Facebook
              {adAccounts.length > 0 && ` (${adAccounts.length} account${adAccounts.length > 1 ? "s" : ""})`}
            </span>
          </div>
        </button>
        <style>{`
          .facebook-auth-button {
            display: flex;
            align-items: center;
            padding: 10px 20px;
            border: 1px solid #ddd;
            border-radius: 4px;
            background: white;
            cursor: pointer;
            font-family: 'Roboto', sans-serif;
            font-size: 14px;
            font-weight: 500;
            transition: background-color 0.2s, box-shadow 0.2s;
          }
          .facebook-auth-button:hover:not(:disabled) {
            background: #f8f8f8;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          .facebook-auth-button:disabled {
            cursor: default;
            opacity: 0.8;
          }
          .facebook-auth-success {
            border-color: #28a745;
            background: #f0fff4;
          }
          .facebook-button-content {
            display: flex;
            align-items: center;
          }
          .facebook-button-icon {
            margin-right: 10px;
            display: flex;
            align-items: center;
          }
          .facebook-button-text {
            color: #333;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", marginLeft: "10px" }}>
      {error && (
        <div style={{ color: "#dc3545", marginBottom: "10px", fontSize: "14px" }}>
          Authentication failed: {error}
        </div>
      )}
      <button
        className="facebook-auth-button"
        onClick={handleAuthClick}
        disabled={isButtonDisabled}
      >
        <div className="facebook-button-content">
          <div className="facebook-button-icon">
            <svg viewBox="0 0 24 24" style={{ width: "18px", height: "18px" }}>
              <path
                fill="#1877F2"
                d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
              />
            </svg>
          </div>
          <span className="facebook-button-text">
            {isButtonDisabled ? "Waiting for Facebook..." : "Sign in with Facebook"}
          </span>
        </div>
      </button>
      <style>{`
        .facebook-auth-button {
          display: flex;
          align-items: center;
          padding: 10px 20px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background: white;
          cursor: pointer;
          font-family: 'Roboto', sans-serif;
          font-size: 14px;
          font-weight: 500;
          transition: background-color 0.2s, box-shadow 0.2s;
          width: fit-content;
        }
        .facebook-auth-button:hover:not(:disabled) {
          background: #f8f8f8;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .facebook-auth-button:disabled {
          cursor: not-allowed;
          opacity: 0.7;
        }
        .facebook-button-content {
          display: flex;
          align-items: center;
        }
        .facebook-button-icon {
          margin-right: 10px;
          display: flex;
          align-items: center;
        }
        .facebook-button-text {
          color: #333;
        }
      `}</style>
    </div>
  );
};

export default FacebookOAuth;
